import { faArrowTurnDown, faArrowTurnUp, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import NavSubItemsToggle from '@/components/NavSubItemsToggle';
import PageAnchorList from '@/components/PageAnchorList';
import type { SidebarNavItem } from '@/components/SidebarNavRail/types';
import AppContext from '@/contexts/appContext';
import { useNarrowNav } from '@/contexts/narrowNavContext';
import { usePageAnchors } from '@/contexts/pageAnchorsContext';
import usePageAnchorJump from '@/hooks/usePageAnchorJump';
import useUserSetting from '@/hooks/useUserSetting';
import { Drawer } from '@/ui';
import resolveCurrentNavLabel from '@/utils/resolveCurrentNavLabel';
import { getAvailableWorkspaceNavOptions } from '@/utils/workspaceNavOptions';

import type { NarrowNavLevel, Props } from './types';

/** Matches SidebarNavRail's link treatment, so the two read as the same navigation. */
const IDLE_ITEM =
  'font-semibold text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-gray-100';
const ACTIVE_ITEM = 'bg-green-50 font-bold text-green-950 dark:bg-green-950! dark:text-green-50';

const NavLink = ({
  item: { label, path, icon },
  current,
  onClick,
  className = ''
}: {
  item: SidebarNavItem;
  current: boolean;
  onClick: () => void;
  className?: string;
}) => (
  <Link
    to={path}
    onClick={onClick}
    aria-current={current ? 'page' : undefined}
    className={`flex items-center gap-x-3 rounded-md px-3 py-2 text-sm ${className} ${
      current ? ACTIVE_ITEM : IDLE_ITEM
    }`}>
    <FontAwesomeIcon
      aria-hidden="true"
      className={`h-5 w-5 shrink-0 ${current ? '' : 'text-gray-600'}`}
      icon={icon}
    />
    <span className="truncate">{label}</span>
  </Link>
);

const FOOTER_BUTTON = `flex w-full items-center gap-x-3 px-4 py-3 text-left text-sm ${IDLE_ITEM}`;

/**
 * The narrow layout's navigation: a menu that slides in from the left of the top bar's hamburger.
 *
 * It opens straight into the level you are already on — the open section's pages, with the cards
 * of the page you are looking at nested under the one that owns the route — rather than making
 * you walk down from the workspace every time. The workspace list is one tap away behind the row
 * pinned to the bottom. Leaving is the top bar's job: its menu button turns into the close, and its
 * avatar is the account menu, so the menu does not carry a second copy of either.
 *
 * Nothing in the workspace list owns a person or a billing route, so the workspace level carries
 * its own row back down into the section you left.
 *
 * It flies out under the top bar rather than over it, at every width: the bar's menu button turns
 * into its close, so it opens and closes from one place. That makes it a docked panel rather than a
 * modal dialog, which would leave the bar inert; the dimmed page around it closes it on a click, as
 * does Escape.
 *
 * It is an alternative to the `<select>` the narrow layout carries by default. Anyone who wants it
 * picks it in Dugout Settings; see `narrowNavContext`.
 */
const NarrowNavDrawer = ({ open, onClose, resetActiveTab }: Props) => {
  const { pathname } = useLocation();
  const { permissions } = useContext(AppContext);
  const { section } = useNarrowNav();
  const { anchors, activeAnchorId } = usePageAnchors();
  const jumpToAnchor = usePageAnchorJump();
  const [anchorsCollapsed, setAnchorsCollapsed] = useUserSetting('navAnchorsCollapsed', false);
  const [level, setLevel] = useState<NarrowNavLevel>('section');

  const workspaceItems = useMemo(() => getAvailableWorkspaceNavOptions(permissions), [permissions]);
  const currentWorkspaceLabel = resolveCurrentNavLabel(workspaceItems, pathname);

  /*
   * Every open starts where the route is, so the menu never reopens on a level someone happened to
   * leave it on. Outside a section the workspace is the only level there is.
   */
  useEffect(() => {
    if (open) setLevel(section ? 'section' : 'workspace');
  }, [open, section]);

  /*
   * Landing on a new route always means the menu has done its job, whichever link did it — the
   * section header is a link of its own, and so is the browser's back button. Compared against the
   * last route rather than left to the dependency list, so an inline `onClose` cannot make this
   * fire on the render that opened the menu.
   */
  const lastPathname = useRef(pathname);

  // A docked panel only hears Escape while focus is inside it; the menu should hear it anywhere.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (lastPathname.current === pathname) return;

    lastPathname.current = pathname;
    onClose();
  }, [onClose, pathname]);

  const showSection = level === 'section' && !!section;
  // The top bar's menu button closes the menu, so the header is left to say where you are.
  const header = showSection ? (
    <div className="min-w-0 flex-1">{section.header}</div>
  ) : section ? (
    /*
     * The way back down. No workspace entry owns a person or a billing route — the workspace list
     * is where you go, not what you are in — so climbing up would otherwise be one-way, with the
     * section only reachable by closing and reopening.
     */
    <button
      type="button"
      onClick={() => setLevel('section')}
      className={`mp-sensitive flex min-w-0 flex-1 items-center gap-x-2 rounded-md px-2 py-2 text-left text-sm ${IDLE_ITEM}`}>
      <FontAwesomeIcon aria-hidden="true" className="h-4 w-4 shrink-0" icon={faArrowTurnDown} />
      <span className="truncate">Back to {section.title}</span>
    </button>
  ) : (
    <span className="text-xs font-semibold tracking-wider text-gray-700 uppercase">Workspace</span>
  );

  /*
   * The step up, while there is one to take — the top bar's button is how you leave, and its
   * avatar is who you are — then, below the rule so it does not read as another level, the
   * extension's tab reset.
   */
  const footer = (
    <>
      {showSection && (
        <>
          <button type="button" onClick={() => setLevel('workspace')} className={FOOTER_BUTTON}>
            <FontAwesomeIcon aria-hidden="true" className="h-4 w-4 shrink-0" icon={faArrowTurnUp} />
            <span className="truncate">Up to workspace</span>
          </button>

          <hr className="border-gray-200" />
        </>
      )}

      {/* Acts on the page the extension is open beside, so it leaves the menu open. */}
      <button type="button" onClick={resetActiveTab} className={FOOTER_BUTTON}>
        <FontAwesomeIcon aria-hidden="true" className="h-4 w-4 shrink-0" icon={faRefresh} />
        <span className="truncate">Reset active tab</span>
      </button>
    </>
  );

  return (
    <>
      {/* Starts under the top bar, so the bar's close stays reachable above it. */}
      {open && (
        <div
          aria-hidden="true"
          onClick={onClose}
          className="fixed inset-x-0 top-8 bottom-0 z-30 bg-gray-900/25"
        />
      )}
      <Drawer
        open={open}
        onClose={onClose}
        side="left"
        modal={false}
        ariaLabel="Navigation menu"
        dataTestId="narrow-nav-drawer"
        className="top-8! h-auto w-[90%] max-w-xs"
        headerClassName="h-14 gap-x-2 px-3 py-0"
        bodyClassName="overflow-x-hidden p-0"
        header={header}
        footer={footer}>
        <nav
          aria-label={showSection ? section.ariaLabel : 'Workspace'}
          className="h-full overflow-y-auto p-2">
          <ul className="space-y-1">
            {showSection
              ? section.items.map((item) => {
                  const current = item.label === section.currentLabel;
                  const hasAnchors = current && anchors.length > 0;

                  return (
                    <li key={item.label}>
                      <div className="relative">
                        <NavLink
                          item={item}
                          current={current}
                          onClick={onClose}
                          className={hasAnchors ? 'pr-10' : ''}
                        />
                        {hasAnchors && (
                          <NavSubItemsToggle
                            collapsed={anchorsCollapsed}
                            onToggle={() => setAnchorsCollapsed(!anchorsCollapsed)}
                            className="absolute top-1/2 right-1.5 h-8 w-8 -translate-y-1/2"
                          />
                        )}
                      </div>
                      {/* The cards of the page you are on, under the entry that owns it. */}
                      {hasAnchors && !anchorsCollapsed && (
                        <PageAnchorList
                          anchors={anchors}
                          activeAnchorId={activeAnchorId}
                          onJump={(id) => {
                            jumpToAnchor(id);
                            onClose();
                          }}
                        />
                      )}
                    </li>
                  );
                })
              : workspaceItems.map((item) => (
                  <li key={item.label}>
                    <NavLink
                      item={item}
                      current={item.label === currentWorkspaceLabel}
                      onClick={onClose}
                    />
                  </li>
                ))}
          </ul>
        </nav>
      </Drawer>
    </>
  );
};

export default React.memo(NarrowNavDrawer);
